import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Edit3, ImagePlus } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { api } from "../lib/api";
import { toast } from "sonner";

const TAGS = ["popular", "new", "offer", "vegan", "spicy"];

// Curated palette — looks good on white & dark menu themes.
const LABEL_COLOR_PALETTE = [
  "#a855f7", // purple
  "#ef4444", // red
  "#f97316", // orange
  "#f59e0b", // amber
  "#eab308", // yellow
  "#22c55e", // green
  "#10b981", // emerald
  "#06b6d4", // cyan
  "#3b82f6", // blue
  "#6366f1", // indigo
  "#ec4899", // pink
  "#64748b", // slate
];

// Compute black/white text for a given hex bg for readability.
function getReadableTextColor(hex) {
  const h = (hex || "#a855f7").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  // YIQ brightness
  return (r * 299 + g * 587 + b * 114) / 1000 >= 140 ? "#111827" : "#ffffff";
}

export default function MenuBuilder() {
  const { id } = useParams();
  const { t } = useLang();
  const [restaurant, setRestaurant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [catDialog, setCatDialog] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catForm, setCatForm] = useState({ name: "", description: "", info_title: "", info_text: "", info_icon: "" });
  const [itemDialog, setItemDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState(initialItem());

  function initialItem() {
    return { name: "", description: "", price: 0, image: "", allergens: [], ingredients: [], tags: [], labels: [], variants: [], extras: [], available: true, category_id: "" };
  }

  const load = async () => {
    const [r, c, i] = await Promise.all([
      api.get(`/restaurants/${id}`),
      api.get(`/restaurants/${id}/categories`),
      api.get(`/restaurants/${id}/items`),
    ]);
    setRestaurant(r.data);
    setCategories(c.data);
    setItems(i.data);
    if (!activeCat && c.data.length) setActiveCat(c.data[0].id);
  };
  useEffect(() => { load(); }, [id]);

  const fileToBase64 = (file) => new Promise((res) => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result);
    reader.readAsDataURL(file);
  });

  const openNewCategory = () => {
    setEditingCat(null);
    setCatForm({ name: "", description: "", info_title: "", info_text: "", info_icon: "" });
    setCatDialog(true);
  };
  const openEditCategory = (c) => {
    setEditingCat(c);
    setCatForm({
      name: c.name || "",
      description: c.description || "",
      info_title: c.info_title || "",
      info_text: c.info_text || "",
      info_icon: c.info_icon || "",
    });
    setCatDialog(true);
  };
  const saveCategory = async (e) => {
    e.preventDefault();
    if (editingCat) {
      await api.put(`/categories/${editingCat.id}`, { ...catForm, sort_order: editingCat.sort_order ?? 0 });
    } else {
      await api.post(`/restaurants/${id}/categories`, { ...catForm, sort_order: categories.length });
    }
    setCatDialog(false);
    setEditingCat(null);
    setCatForm({ name: "", description: "", info_title: "", info_text: "", info_icon: "" });
    load();
    toast.success("Saved");
  };
  const deleteCategory = async (cid) => {
    if (!window.confirm("Delete category?")) return;
    await api.delete(`/categories/${cid}`);
    load();
  };

  const openNewItem = () => {
    setEditingItem(null);
    setItemForm({ ...initialItem(), category_id: activeCat });
    setItemDialog(true);
  };
  const openEditItem = (it) => {
    setEditingItem(it);
    setItemForm({
      ...initialItem(),
      ...it,
      labels: Array.isArray(it.labels) ? it.labels : [],
      variants: Array.isArray(it.variants) ? it.variants : [],
      extras: Array.isArray(it.extras) ? it.extras : [],
    });
    setItemDialog(true);
  };
  const saveItem = async (e) => {
    e.preventDefault();
    const cleanLabels = (itemForm.labels || [])
      .map((l) => ({ text: (l.text || "").trim(), color: l.color || "#a855f7" }))
      .filter((l) => l.text);
    const cleanVariants = (itemForm.variants || [])
      .map((v) => ({ name: (v.name || "").trim(), price_delta: parseFloat(v.price_delta) || 0 }))
      .filter((v) => v.name);
    const cleanExtras = (itemForm.extras || [])
      .map((e) => ({ name: (e.name || "").trim(), price: parseFloat(e.price) || 0 }))
      .filter((e) => e.name);
    const payload = { ...itemForm, price: parseFloat(itemForm.price) || 0, labels: cleanLabels, variants: cleanVariants, extras: cleanExtras };
    if (editingItem) await api.put(`/items/${editingItem.id}`, payload);
    else await api.post(`/restaurants/${id}/items`, { ...payload, sort_order: items.length });
    setItemDialog(false);
    load();
    toast.success("Saved");
  };
  const deleteItem = async (iid) => {
    if (!window.confirm("Delete item?")) return;
    await api.delete(`/items/${iid}`);
    load();
  };
  const handleItemImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const b64 = await fileToBase64(file);
    setItemForm({ ...itemForm, image: b64 });
  };
  const toggleTag = (tag) => {
    const has = itemForm.tags.includes(tag);
    setItemForm({ ...itemForm, tags: has ? itemForm.tags.filter((x) => x !== tag) : [...itemForm.tags, tag] });
  };
  const addLabel = () => {
    setItemForm({
      ...itemForm,
      labels: [...(itemForm.labels || []), { text: "", color: LABEL_COLOR_PALETTE[(itemForm.labels?.length || 0) % LABEL_COLOR_PALETTE.length] }],
    });
  };
  const updateLabel = (idx, patch) => {
    const next = [...(itemForm.labels || [])];
    next[idx] = { ...next[idx], ...patch };
    setItemForm({ ...itemForm, labels: next });
  };
  const removeLabel = (idx) => {
    const next = [...(itemForm.labels || [])];
    next.splice(idx, 1);
    setItemForm({ ...itemForm, labels: next });
  };
  // Variants (size options)
  const addVariant = () => setItemForm({ ...itemForm, variants: [...(itemForm.variants || []), { name: "", price_delta: 0 }] });
  const updateVariant = (idx, patch) => {
    const next = [...(itemForm.variants || [])];
    next[idx] = { ...next[idx], ...patch };
    setItemForm({ ...itemForm, variants: next });
  };
  const removeVariant = (idx) => {
    const next = [...(itemForm.variants || [])];
    next.splice(idx, 1);
    setItemForm({ ...itemForm, variants: next });
  };
  // Extras (add-ons)
  const addExtra = () => setItemForm({ ...itemForm, extras: [...(itemForm.extras || []), { name: "", price: 0 }] });
  const updateExtra = (idx, patch) => {
    const next = [...(itemForm.extras || [])];
    next[idx] = { ...next[idx], ...patch };
    setItemForm({ ...itemForm, extras: next });
  };
  const removeExtra = (idx) => {
    const next = [...(itemForm.extras || [])];
    next.splice(idx, 1);
    setItemForm({ ...itemForm, extras: next });
  };

  const visibleItems = items.filter((i) => !activeCat || i.category_id === activeCat);
  const currency = restaurant?.currency || "EUR";
  const symbol = { EUR: "€", USD: "$", TRY: "₺", AED: "AED" }[currency];

  return (
    <DashboardLayout restaurantId={id}>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{t("dashboard.menuBuilder")}</div>
            <h1 className="text-3xl font-bold tracking-tight">{restaurant?.name}</h1>
          </div>
          <div className="flex gap-2">
            <Dialog open={catDialog} onOpenChange={(o) => { setCatDialog(o); if (!o) setEditingCat(null); }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" onClick={openNewCategory} data-testid="add-category-btn"><Plus className="h-4 w-4 mr-2" />{t("menu.addCategory")}</Button>
              </DialogTrigger>
              <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{editingCat ? t("menu.editCategory") : t("menu.addCategory")}</DialogTitle></DialogHeader>
                <form onSubmit={saveCategory} className="space-y-4" data-testid="category-form">
                  <div className="space-y-1.5">
                    <Label>{t("menu.categoryName")}</Label>
                    <Input required value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} className="rounded-xl" data-testid="cat-name-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("restaurant.description")}</Label>
                    <Input value={catForm.description} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })} className="rounded-xl" />
                  </div>
                  <div className="rounded-2xl border bg-secondary/30 p-3 space-y-3">
                    <div>
                      <div className="font-semibold text-sm">{t("menu.infoBox")}</div>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{t("menu.infoBoxHint")}</p>
                    </div>
                    <div className="grid grid-cols-[80px_1fr] gap-2">
                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("menu.infoIcon")}</Label>
                        <Input
                          value={catForm.info_icon}
                          onChange={(e) => setCatForm({ ...catForm, info_icon: e.target.value })}
                          placeholder="🍔"
                          maxLength={4}
                          className="rounded-xl text-center text-lg"
                          data-testid="cat-info-icon-input"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("menu.infoTitle")}</Label>
                        <Input
                          value={catForm.info_title}
                          onChange={(e) => setCatForm({ ...catForm, info_title: e.target.value })}
                          placeholder="Burger Menü"
                          className="rounded-xl"
                          data-testid="cat-info-title-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">{t("menu.infoText")}</Label>
                      <Textarea
                        value={catForm.info_text}
                        onChange={(e) => setCatForm({ ...catForm, info_text: e.target.value })}
                        rows={4}
                        placeholder={"Alle Burger auch im Menü erhältlich!\nInkl. Pommes mit Dip nach Wahl\n\n+4,50 €"}
                        className="rounded-xl"
                        data-testid="cat-info-text-input"
                      />
                    </div>
                    {(catForm.info_title || catForm.info_text) && (
                      <div className="rounded-xl border-2 border-dashed border-purple-300/50 bg-purple-50/60 dark:bg-purple-500/10 p-3" data-testid="cat-info-preview">
                        <div className="flex items-start gap-2.5">
                          {catForm.info_icon && <span className="text-2xl leading-none" aria-hidden>{catForm.info_icon}</span>}
                          <div className="min-w-0 flex-1">
                            {catForm.info_title && <div className="font-bold text-sm">{catForm.info_title}</div>}
                            {catForm.info_text && <div className="text-xs whitespace-pre-wrap opacity-80 mt-0.5">{catForm.info_text}</div>}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <DialogFooter><Button type="submit" className="rounded-full bg-purple-600 text-white" data-testid="cat-submit-btn">{t("common.save")}</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            <Button onClick={openNewItem} className="rounded-full bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-500/25" disabled={categories.length === 0} data-testid="add-item-btn">
              <Plus className="h-4 w-4 mr-2" />{t("menu.addItem")}
            </Button>
          </div>
        </div>

        {/* Categories tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setActiveCat(c.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-all whitespace-nowrap ${
                activeCat === c.id ? "bg-purple-600 text-white border-purple-600 shadow-lg shadow-purple-500/25" : "bg-card hover:border-purple-500/30"
              }`}
              data-testid={`cat-tab-${c.id}`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {/* Items grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleItems.map((it) => (
            <div key={it.id} className="rounded-2xl border bg-card overflow-hidden" data-testid={`item-card-${it.id}`}>
              <div className="aspect-[4/3] bg-secondary relative overflow-hidden">
                {it.image ? <img src={it.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center"><ImagePlus className="h-8 w-8 text-muted-foreground" /></div>}
                {!it.available && <div className="absolute inset-0 bg-black/50 grid place-items-center"><span className="text-white text-xs font-bold uppercase tracking-widest">{t("menu.unavailable")}</span></div>}
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start gap-2">
                  <h3 className="font-bold">{it.name}</h3>
                  <span className="font-bold text-purple-600 dark:text-purple-400 text-sm whitespace-nowrap">{it.price.toFixed(2)} {symbol}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{it.description}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {it.tags.map((tg) => <Badge key={tg} variant="secondary" className="text-[10px] rounded-full">{t(`menu.tag${tg.charAt(0).toUpperCase() + tg.slice(1)}`)}</Badge>)}
                  {(it.labels || []).map((lb, idx) => (
                    <span
                      key={`lb-${idx}`}
                      className="text-[10px] rounded-full px-2 py-0.5 font-semibold"
                      style={{ backgroundColor: lb.color, color: getReadableTextColor(lb.color) }}
                      data-testid={`item-card-label-${it.id}-${idx}`}
                    >
                      {lb.text}
                    </span>
                  ))}
                </div>
                <div className="flex gap-1 mt-3">
                  <Button size="sm" variant="outline" className="flex-1 rounded-full" onClick={() => openEditItem(it)} data-testid={`item-edit-${it.id}`}><Edit3 className="h-3 w-3" /></Button>
                  <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => deleteItem(it.id)} data-testid={`item-delete-${it.id}`}><Trash2 className="h-3 w-3" /></Button>
                </div>
              </div>
            </div>
          ))}
          {visibleItems.length === 0 && (
            <div className="col-span-full rounded-2xl border border-dashed p-12 text-center text-muted-foreground">
              {categories.length === 0 ? "→ " + t("menu.addCategory") : t("menu.addItem")}
            </div>
          )}
        </div>

        {/* Categories list with delete */}
        {categories.length > 0 && (
          <div className="rounded-2xl border bg-card p-4">
            <h3 className="font-semibold mb-2 text-sm uppercase tracking-widest text-muted-foreground">{t("menu.categories")}</h3>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <div key={c.id} className="px-3 py-1.5 rounded-full bg-secondary text-sm flex items-center gap-2" data-testid={`cat-row-${c.id}`}>
                  {c.info_icon && <span aria-hidden>{c.info_icon}</span>}
                  {c.name}
                  <button onClick={() => openEditCategory(c)} className="text-muted-foreground hover:text-foreground transition-colors" aria-label={t("menu.editCategory")} data-testid={`cat-edit-${c.id}`}><Edit3 className="h-3 w-3" /></button>
                  <button onClick={() => deleteCategory(c.id)} className="text-destructive hover:opacity-70" data-testid={`cat-delete-${c.id}`}><Trash2 className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Item Dialog */}
        <Dialog open={itemDialog} onOpenChange={setItemDialog}>
          <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingItem ? t("common.edit") : t("menu.addItem")}</DialogTitle></DialogHeader>
            <form onSubmit={saveItem} className="space-y-4" data-testid="item-form">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2">
                  <Label>{t("menu.itemName")}</Label>
                  <Input required value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} className="rounded-xl" data-testid="item-name-input" />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label>{t("menu.itemDesc")}</Label>
                  <Textarea value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("menu.itemPrice")} ({symbol})</Label>
                  <Input type="number" step="0.01" required value={itemForm.price} onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })} className="rounded-xl" data-testid="item-price-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Kategorie</Label>
                  <select className="w-full h-10 rounded-xl border bg-background px-3" value={itemForm.category_id} onChange={(e) => setItemForm({ ...itemForm, category_id: e.target.value })} required data-testid="item-cat-select">
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label>{t("menu.allergens")} ({t("menu.separateByComma")})</Label>
                  <Input value={itemForm.allergens.join(", ")} onChange={(e) => setItemForm({ ...itemForm, allergens: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="rounded-xl" />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label>{t("menu.ingredients")} ({t("menu.separateByComma")})</Label>
                  <Input value={itemForm.ingredients.join(", ")} onChange={(e) => setItemForm({ ...itemForm, ingredients: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="rounded-xl" />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label>{t("menu.tags")}</Label>
                  <div className="flex flex-wrap gap-2">
                    {TAGS.map((tg) => (
                      <button type="button" key={tg} onClick={() => toggleTag(tg)} className={`px-3 py-1 rounded-full text-xs border ${itemForm.tags.includes(tg) ? "bg-purple-600 text-white border-purple-600" : "bg-background"}`} data-testid={`item-tag-${tg}`}>
                        {t(`menu.tag${tg.charAt(0).toUpperCase() + tg.slice(1)}`)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2 col-span-2">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <Label>{t("menu.customLabels")}</Label>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{t("menu.customLabelsHint")}</p>
                    </div>
                    <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={addLabel} data-testid="add-label-btn">
                      <Plus className="h-3 w-3 mr-1" />{t("menu.addLabel")}
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(itemForm.labels || []).map((lb, idx) => (
                      <div key={idx} className="rounded-xl border p-2.5 bg-background/60 space-y-2" data-testid={`label-row-${idx}`}>
                        <div className="flex gap-2 items-stretch">
                          <Input
                            value={lb.text}
                            onChange={(e) => updateLabel(idx, { text: e.target.value })}
                            placeholder={t("menu.labelText")}
                            className="rounded-xl flex-1"
                            data-testid={`label-text-${idx}`}
                          />
                          <button
                            type="button"
                            onClick={() => removeLabel(idx)}
                            className="px-2 rounded-xl border text-destructive hover:bg-destructive/10 transition-colors"
                            aria-label={t("menu.removeLabel")}
                            data-testid={`label-remove-${idx}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[11px] text-muted-foreground">{t("menu.labelColor")}:</span>
                          {LABEL_COLOR_PALETTE.map((c) => (
                            <button
                              type="button"
                              key={c}
                              onClick={() => updateLabel(idx, { color: c })}
                              className={`h-6 w-6 rounded-full border-2 transition-transform ${lb.color === c ? "scale-110 border-foreground" : "border-transparent"}`}
                              style={{ backgroundColor: c }}
                              aria-label={c}
                              data-testid={`label-color-${idx}-${c}`}
                            />
                          ))}
                          <span
                            className="ml-auto text-[11px] rounded-full px-2 py-0.5 font-semibold"
                            style={{ backgroundColor: lb.color, color: getReadableTextColor(lb.color) }}
                          >
                            {lb.text || t("menu.labelText").split(" (")[0]}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Variants (Premium ordering feature) */}
                <div className="space-y-2 col-span-2">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <Label>{t("menuBuilder.variants")}</Label>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{t("menuBuilder.variantsHint")}</p>
                    </div>
                    <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={addVariant} data-testid="add-variant-btn">
                      <Plus className="h-3 w-3 mr-1" />{t("menuBuilder.addVariant")}
                    </Button>
                  </div>
                  {(itemForm.variants || []).length > 0 && (
                    <div className="space-y-2">
                      {(itemForm.variants || []).map((v, idx) => (
                        <div key={idx} className="flex gap-2 items-center" data-testid={`variant-row-${idx}`}>
                          <Input
                            value={v.name}
                            onChange={(e) => updateVariant(idx, { name: e.target.value })}
                            placeholder={t("menuBuilder.variantName")}
                            className="rounded-xl flex-1"
                            data-testid={`variant-name-${idx}`}
                          />
                          <div className="relative w-32">
                            <Input
                              type="number"
                              step="0.10"
                              value={v.price_delta}
                              onChange={(e) => updateVariant(idx, { price_delta: e.target.value })}
                              placeholder={t("menuBuilder.variantDelta")}
                              className="rounded-xl pr-6"
                              data-testid={`variant-delta-${idx}`}
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs opacity-60">€</span>
                          </div>
                          <button type="button" onClick={() => removeVariant(idx)} className="px-2 rounded-xl border text-destructive hover:bg-destructive/10 transition-colors" data-testid={`variant-remove-${idx}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Extras (Premium ordering feature) */}
                <div className="space-y-2 col-span-2">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <Label>{t("menuBuilder.extras")}</Label>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{t("menuBuilder.extrasHint")}</p>
                    </div>
                    <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={addExtra} data-testid="add-extra-btn">
                      <Plus className="h-3 w-3 mr-1" />{t("menuBuilder.addExtra")}
                    </Button>
                  </div>
                  {(itemForm.extras || []).length > 0 && (
                    <div className="space-y-2">
                      {(itemForm.extras || []).map((ex, idx) => (
                        <div key={idx} className="flex gap-2 items-center" data-testid={`extra-row-${idx}`}>
                          <Input
                            value={ex.name}
                            onChange={(e) => updateExtra(idx, { name: e.target.value })}
                            placeholder={t("menuBuilder.extraName")}
                            className="rounded-xl flex-1"
                            data-testid={`extra-name-${idx}`}
                          />
                          <div className="relative w-32">
                            <Input
                              type="number"
                              step="0.10"
                              value={ex.price}
                              onChange={(e) => updateExtra(idx, { price: e.target.value })}
                              placeholder={t("menuBuilder.extraPrice")}
                              className="rounded-xl pr-6"
                              data-testid={`extra-price-${idx}`}
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs opacity-60">€</span>
                          </div>
                          <button type="button" onClick={() => removeExtra(idx)} className="px-2 rounded-xl border text-destructive hover:bg-destructive/10 transition-colors" data-testid={`extra-remove-${idx}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label>{t("menu.itemImage")}</Label>
                  <div className="flex items-center gap-3">
                    <div className="w-20 h-20 rounded-xl border bg-secondary overflow-hidden">
                      {itemForm.image && <img src={itemForm.image} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <label className="cursor-pointer">
                      <Input type="file" accept="image/*" className="hidden" onChange={handleItemImage} data-testid="item-image-upload" />
                      <Button type="button" variant="outline" className="rounded-full pointer-events-none">Upload</Button>
                    </label>
                  </div>
                </div>
                <div className="col-span-2 flex items-center justify-between rounded-xl border p-3">
                  <Label>{t("menu.available")}</Label>
                  <Switch checked={itemForm.available} onCheckedChange={(v) => setItemForm({ ...itemForm, available: v })} data-testid="item-available-switch" />
                </div>
              </div>
              <DialogFooter><Button type="submit" className="rounded-full bg-purple-600 text-white" data-testid="item-submit-btn">{t("common.save")}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
